import { apiClient } from './apiClient';

export interface ClientImageAssetCategoryCount {
  category: string;
  count: number;
}

export interface ClientImageAssetSample {
  id: string;
  url: string | null;
  alt_text: string;
  description: string;
  category: string;
  tags: string[];
  filename: string;
  extension: string | null;
}

export interface ClientImageAssetsSummary {
  tenant_id: string;
  total: number;
  needs_review: number;
  web_displayable: number;
  by_category: ClientImageAssetCategoryCount[];
  sample: ClientImageAssetSample[];
}

export interface ClientImageAssetListItem {
  id: string;
  filename: string;
  alt_text: string;
  description: string;
  category: string;
  tags: string[];
  extension: string | null;
  label_status: string;
  state: string;
  url: string | null;
  size_bytes: number;
  source_path: string;
  label_source: string;
  reviewed_at: string | null;
  archived_at: string | null;
  created_at: string;
}

export interface ClientImageAssetDetail extends ClientImageAssetListItem {
  original_path: string;
  relative_path: string;
  client_folder: string;
  client_channel: string;
  image_id: string;
  source_sha256: string;
  storage_path: string;
  storage_upload_status: string;
  storage_uploaded_at: string | null;
  label_run_id: string;
  reviewed_by: string;
  archive_reason: string;
  updated_at: string;
  original_url: string | null;
}

export interface ClientImageAssetsList {
  tenant_id: string;
  total: number;
  limit: number;
  offset: number;
  items: ClientImageAssetListItem[];
}

/**
 * Response of POST /client-image-assets/:id/editor-copy — a durable, public
 * copy of a web-ready asset suitable for embedding in an article. The signed
 * `url` returned by the list endpoint expires (~300s) and MUST NOT be inserted
 * into a document; this endpoint returns a stable `url` instead.
 */
export interface ClientImageAssetEditorCopy {
  asset_id: string;
  url: string;
  alt_text: string;
  description: string;
  filename: string;
  category: string;
}

/**
 * Response of POST /client-image-assets/resolve-brand-image. `resolved: false`
 * means no confident logo was found and the server changed nothing — the
 * Media empty state should stay as-is.
 */
export interface BrandImageResolveResponse {
  tenant_id: string;
  resolved: boolean;
  source: 'wp_logo' | 'homepage' | 'image_search' | null;
  asset_id: string | null;
  url: string | null;
  detail: string;
}

/** Response of POST /client-image-assets/from-url — import by URL/Drive link. */
export interface ClientImageAssetImportUrlResponse {
  asset_id: string;
  filename: string;
  extension: string | null;
  web_renderable: boolean;
  size_bytes: number | null;
}

/** Per-file result + summary from POST /client-image-assets/upload. */
export interface ClientImageAssetUploadResponse {
  tenant_id: string;
  received: number;
  stored: number;
  rejected: number;
  results: Array<{
    filename: string;
    status: string;
    reason?: string;
    asset_id?: string;
    extension?: string | null;
  }>;
}

/** Response of POST /client-image-assets/:id/convert — HEIC → new JPEG pool asset. */
export interface ClientImageAssetConvertResponse {
  reused: boolean;
  asset_id: string;
  filename: string;
  extension: string;
  width: number | null;
  height: number | null;
}

/** Response of POST /client-image-assets/sync-from-drive. Counts are truthful:
 * `ingested` only counts persisted rows; `cap_hit` + `remaining` mean the run
 * stopped at the per-run cap and re-running continues safely (dedup by Drive
 * file ID). */
export interface DriveSyncResponse {
  tenant_id: string;
  folder_id: string;
  listed: number;
  ingested: number;
  skipped_duplicates: number;
  skipped_unsupported: number;
  failed: number;
  remaining: number;
  cap_hit: boolean;
  message: string;
  failures: Array<{ file_id: string; name: string; reason: string }>;
  unsupported: Array<{ file_id: string; name: string; extension?: string | null }>;
}

/** Response of GET /client-image-assets/sync-from-drive/config. */
export interface DriveSyncConfig {
  enabled: boolean;
  service_account_email: string | null;
}

/** A whole-folder sync run downloads/uploads/thumbnails up to 200 files —
 * wall time is minutes, not the apiClient default 30s. */
const DRIVE_SYNC_TIMEOUT_MS = 10 * 60 * 1000;

export interface ClientImageAssetListParams {
  limit?: number;
  offset?: number;
  q?: string;
  category?: string;
  label_status?: string;
  extension?: string;
  state?: string;
  include_archived?: boolean;
  /** Also include freshly-added needs_review rows in the default view. */
  include_review?: boolean;
  sign?: boolean;
}

class ClientImageAssetsService {
  async getSummary(tenantId: string): Promise<ClientImageAssetsSummary> {
    const { data } = await apiClient.get<ClientImageAssetsSummary>(
      '/client-image-assets/summary',
      { params: { tenant_id: tenantId, sample_limit: 6 } }
    );
    return data;
  }

  async list(
    tenantId: string,
    params: ClientImageAssetListParams = {}
  ): Promise<ClientImageAssetsList> {
    const { data } = await apiClient.get<ClientImageAssetsList>(
      '/client-image-assets',
      { params: { tenant_id: tenantId, ...params } }
    );
    return data;
  }

  async getById(
    tenantId: string,
    id: string
  ): Promise<ClientImageAssetDetail> {
    const { data } = await apiClient.get<ClientImageAssetDetail>(
      '/client-image-assets/' + id,
      { params: { tenant_id: tenantId } }
    );
    return data;
  }

  /**
   * Resolve a real brand logo for the tenant (WP-configured logo → homepage
   * crawl → same-domain image search), ingest it into the governed asset
   * library, and set it as the primary brand image. When `resolved` is false
   * nothing was changed server-side (no-guess policy) — leave the empty state.
   */
  async resolveBrandImage(tenantId: string): Promise<BrandImageResolveResponse> {
    const { data } = await apiClient.post<BrandImageResolveResponse>(
      `/client-image-assets/resolve-brand-image?tenant_id=${encodeURIComponent(
        tenantId
      )}`
    );
    return data;
  }

  /**
   * Copy a web-ready asset to a durable public URL for use in the editor and
   * return the stable URL + metadata. Called when the user picks an asset in the
   * editor's asset-library dialog; the returned `url` (not the signed list URL)
   * is what gets written into the document.
   */
  async selectForEditor(
    tenantId: string,
    assetId: string
  ): Promise<ClientImageAssetEditorCopy> {
    const { data } = await apiClient.post<ClientImageAssetEditorCopy>(
      `/client-image-assets/${assetId}/editor-copy?tenant_id=${encodeURIComponent(
        tenantId
      )}`
    );
    return data;
  }

  /**
   * Convert a HEIC/HEIF pool image to a full-resolution JPEG pool asset
   * (previewable AND Google-Ads-pushable). Idempotent — a repeat convert
   * returns the existing JPEG. Flag-gated server-side
   * (CLIENT_IMAGE_HEIC_CONVERT_ENABLED); returns 503 when disabled.
   */
  async convertHeic(
    tenantId: string,
    assetId: string
  ): Promise<ClientImageAssetConvertResponse> {
    const { data } = await apiClient.post<ClientImageAssetConvertResponse>(
      `/client-image-assets/${assetId}/convert?tenant_id=${encodeURIComponent(
        tenantId
      )}`
    );
    return data;
  }

  /**
   * Import an image into the pool from a direct URL or a Google Drive share
   * link ("Anyone with the link"). Fetched + validated + stored server-side.
   */
  async importFromUrl(
    tenantId: string,
    url: string,
    opts: { category?: string; alt_text?: string; description?: string; tags?: string } = {}
  ): Promise<ClientImageAssetImportUrlResponse> {
    const { data } = await apiClient.post<ClientImageAssetImportUrlResponse>(
      `/client-image-assets/from-url?tenant_id=${encodeURIComponent(tenantId)}`,
      { url, ...opts }
    );
    return data;
  }

  /**
   * Pull the tenant's connected Drive folder into the governed pool
   * (operator-triggered, on-demand). Server-side flag DRIVE_ASSET_SYNC_ENABLED
   * (503 when off). Uses an extended per-request timeout — and even if the
   * request drops, the server run completes and an idempotent re-run/refresh
   * shows the outcome (never treat a timeout as data loss).
   */
  async syncFromDrive(tenantId: string): Promise<DriveSyncResponse> {
    const { data } = await apiClient.post<DriveSyncResponse>(
      `/client-image-assets/sync-from-drive?tenant_id=${encodeURIComponent(
        tenantId
      )}`,
      undefined,
      { timeout: DRIVE_SYNC_TIMEOUT_MS }
    );
    return data;
  }

  /** SA email for the share instruction + whether the sync flag is on. */
  async getDriveSyncConfig(tenantId: string): Promise<DriveSyncConfig> {
    const { data } = await apiClient.get<DriveSyncConfig>(
      '/client-image-assets/sync-from-drive/config',
      { params: { tenant_id: tenantId } }
    );
    return data;
  }

  /** Upload one or more local image files into the governed pool (multipart). */
  async uploadFiles(
    tenantId: string,
    files: File[],
    opts: { category?: string; alt_text?: string; description?: string; tags?: string } = {}
  ): Promise<ClientImageAssetUploadResponse> {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    if (opts.category) form.append('category', opts.category);
    if (opts.alt_text) form.append('alt_text', opts.alt_text);
    if (opts.description) form.append('description', opts.description);
    if (opts.tags) form.append('tags', opts.tags);
    const { data } = await apiClient.post<ClientImageAssetUploadResponse>(
      `/client-image-assets/upload?tenant_id=${encodeURIComponent(tenantId)}`,
      form,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return data;
  }
}

export const clientImageAssetsService = new ClientImageAssetsService();
